import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { trackEvent } from "@/lib/track";

const CartContext = createContext(null);
const KEY = "lumiere_cart";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const [shippingPreview, setShippingPreview] = useState({ price: null, free_threshold: 100, free: false, label: null });
  const [paymentEnabled, setPaymentEnabled] = useState(true);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    api.get("/site-config")
      .then(({ data }) => setPaymentEnabled(data?.payment_enabled !== false))
      .catch(() => {});
  }, []);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);

  // Fetch cheapest shipping option for FR (default zone) — used as cart drawer preview
  useEffect(() => {
    if (!items.length) {
      setShippingPreview({ price: null, free_threshold: 100, free: false, label: null });
      return;
    }
    api.get("/shipping/options", { params: { country: "FR", subtotal } })
      .then(({ data }) => {
        const opts = data?.options || [];
        if (!opts.length) {
          setShippingPreview({ price: null, free_threshold: 100, free: false, label: null });
          return;
        }
        // cheapest non-zero, else first option
        const sorted = [...opts].sort((a, b) => a.price - b.price);
        const pick = sorted[0];
        setShippingPreview({
          price: pick.price,
          free: pick.free,
          label: pick.label,
          free_threshold: 100, // generic, for UI hint
        });
      })
      .catch(() => {});
  }, [items.length, subtotal]);

  const addItem = useCallback((product, quantity = 1) => {
    trackEvent("add_to_cart", {
      product_id: product.id,
      product_name: product.name,
      slug: product.slug,
      quantity,
    });
    setItems((prev) => {
      const found = prev.find((i) => i.product_id === product.id);
      if (found) {
        return prev.map((i) =>
          i.product_id === product.id ? { ...i, quantity: i.quantity + quantity } : i
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          slug: product.slug,
          name: product.name,
          price: product.price,
          image: product.image,
          quantity,
        },
      ];
    });
    setOpen(true);
  }, []);

  const updateQty = useCallback((productId, quantity) => {
    setItems((prev) =>
      quantity <= 0
        ? prev.filter((i) => i.product_id !== productId)
        : prev.map((i) => (i.product_id === productId ? { ...i, quantity } : i))
    );
  }, []);

  const removeItem = useCallback((productId) => {
    setItems((prev) => prev.filter((i) => i.product_id !== productId));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider
      value={{ items, count, subtotal, open, setOpen, addItem, updateQty, removeItem, clear, shippingPreview, paymentEnabled }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
